import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useModel } from "hooks/useModel";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ScrollContainer from "components/common/ScrollList";
import { fetchPrompts } from "../../store/PromptActions";
import { useAxios } from "myAxios";
import { TabContext, TabPanel } from "@mui/lab";
import styles from "./PromptsCard.module.scss";
import PromptsCardList from "./PromptsCardList";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";

const tabsxStyles = {
  "& .MuiTab-root": {
    minWidth: 0,
    padding: "0 12px",
    fontSize: "12px",
    "&.Mui-selected": {
      color: "#111827",
      fontWeight: 600,
    },
    "&:hover": {
      color: "#111827",
    },
  },
  "& .MuiTabs-indicator": {
    height: 3,
    borderRadius: "3px 3px 0 0",
    bgcolor: "primary.main",
  },
};
const PromptsCard = () => {
  const {
    prompts,
    setPrompts,
    setPromptsOpen,
    setPromptConfig,
    searchPromptList,
    setSearchPromptList,
  } = useModel("aichat");
  const [selectedTab, setSelectedTab] = useState<string>("All");
  const [hasMore, setHasMore] = useState(true);
  const [value, setValue] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const axios = useAxios();
  const tabPanelContainerRef = useRef<HTMLDivElement>(null);

  const onTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
    const currentTab = prompts.find((item) => item.type === newValue);
    setHasMore(currentTab?.hasMore || false);
    if (tabPanelContainerRef.current) {
      tabPanelContainerRef.current.scrollTop = 0;
    }
  };

  useEffect(() => {
    if (!value.trim()) {
      setSearchPromptList([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);

    const debouncedFetch = debounce(async () => {
      try {
        const res = await fetchPrompts(axios, {
          pageNum: 0,
          pageSize: 50,
          promptType: selectedTab === "system" ? selectedTab : undefined,
          favorite: selectedTab === "favorite" ? true : undefined,
          promptVisibility:
            selectedTab === "public" || selectedTab === "private" ? selectedTab : undefined,
          keyword: value.trim(),
        });
        setSearchPromptList(res.prompts || []);
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    debouncedFetch();
    return () => {
      debouncedFetch.cancel();
    };
  }, [value, selectedTab]);

  const onLoadMore = async (type: string) => {
    try {
      const currentTab = prompts.find((item) => item.type === selectedTab);
      const pageNum = currentTab?.nextpage ?? 0;
      const res = await fetchPrompts(axios, {
        pageNum,
        pageSize: 15,
        promptType: type === "system" ? type : undefined,
        favorite: type === "favorite" ? true : undefined,
        promptVisibility: type === "public" || type === "private" ? type : undefined,
      });
      setPrompts((prev) =>
        prev.map((item) => {
          if (item.type === selectedTab) {
            return {
              ...item,
              list: Array.from(
                new Map([...item.list, ...res.prompts].map((item) => [item.id, item])).values()
              ) as any,
              hasMore: res.hasMore,
              nextpage: pageNum + 1,
            };
          }
          return item;
        })
      );
      setHasMore(res.hasMore);
    } catch (error) {
      console.error("Failed to load more prompts:", error);
    }
  };

  return (
    <Box className={styles.PromptsCard}>
      <Box className={styles.header}>
        <div>Quick Prompts</div>
        <IconButton onClick={() => setPromptsOpen(false)}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box className={styles.content}>
        <Box className={styles.promptsTabs}>
          <TabContext value={selectedTab}>
            <Tabs
              value={selectedTab}
              onChange={onTabChange}
              sx={tabsxStyles}
            >
              {prompts.map((i) => (
                <Tab
                  key={i.type}
                  label={i.type}
                  value={i.type}
                />
              ))}
            </Tabs>
            <Box className={styles.searchBox}>
              <TextField
                id="input-with-icon-adornment"
                placeholder="Search prompts"
                variant="outlined"
                fullWidth
                value={value}
                onChange={(e) => setValue(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </Box>
            {value.trim() ? (
              <Box className={styles.tabPanelContainer}>
                <PromptsCardList
                  data={{
                    type: "search",
                    list: searchPromptList,
                    hasMore: false,
                    nextpage: 0,
                  }}
                />
              </Box>
            ) : (
              <Box
                className={styles.tabPanelContainer}
                ref={tabPanelContainerRef}
              >
                {prompts.map((i) => (
                  <TabPanel
                    key={i.type}
                    value={i.type}
                  >
                    <ScrollContainer
                      renderContent={<PromptsCardList data={i} />}
                      onLoadMore={() => onLoadMore(i.type)}
                      hasMore={hasMore}
                    />
                  </TabPanel>
                ))}
              </Box>
            )}
          </TabContext>
        </Box>
      </Box>
      <Box
        className={styles.footer}
        onClick={() =>
          setPromptConfig({
            state: "New",
            favorite: true,
            promptContent: "",
            promptDescription: "",
            promptIcon: "lightbulb",
            promptTitle: "",
            promptType: "personal",
            promptVisibility: "public",
            id: null,
          })
        }
      >
        <Button variant="contained">+ new prompt</Button>
      </Box>
    </Box>
  );
};

export default PromptsCard;
